import { Injectable } from '@angular/core';
import { catchError, map, type Observable } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { type AccountDetail } from './../models/account-detail';
import { type MailingAddress } from '../models/mailing-address';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(private readonly http: HttpClient) {}

  getAccountDetail(acctNumber: string, userName: string): Observable<AccountDetail> {
    const url = environment.OASUrl() + '/account/account-details';
    const req = {
      AccountId: acctNumber,
      WebUserId: userName,
    };

    const res = this.http.post<AccountDetail>(url, req);
    return res;
  }

  GetPremiseByAddress(addressRequest: any): Observable<any> {
    const url = environment.OASUrl() + '/account/get-premise-by-address';
    const res = this.http.post(url, addressRequest);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  GetPerson(req: any): Observable<any> {
    const url = environment.OASUrl() + '/account/get-person-info';
    const res = this.http.post(url, req);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        }
      }),
    );
  }

  GetAutoPayDetails(accountNumber: string): Observable<any> {
    const url = environment.OASUrl() + '/autopay/get-autopay?accountNumber=' + accountNumber;
    const res = this.http.get(url);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        }
      }),
    );
  }

  GetHeadlineCode(accountNumber: string): Observable<any> {
    const url = environment.OASUrl() + '/account/get-headline-code?accountNumber=' + accountNumber;
    const res = this.http.get(url);
    return res.pipe(
      map((data: any) => {
        return data;
      }),
    );
  }

  GetPayPlanDetails(accountNumber: string): Observable<any> {
    const url = environment.OASUrl() + '/payplan/get-payplan?accountNumber=' + accountNumber;
    const res = this.http.get(url);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        }
      }),
    );
  }

  UpdatePersonContact(person: any): Observable<any> {
    const url = environment.OASUrl() + '/account/update-contact';
    const res = this.http.post(url, person);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  ChangePassword(changePasswordRequest: any): Observable<any> {
    const url = environment.OASUrl() + '/account/update-password';
    const res = this.http.post(url, changePasswordRequest);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  DeleteOASAccount(accountNumber: string, userName: string): Observable<any> {
    const url =
      environment.OASUrl() + '/account/delete-oas-account?accountNumber=' + accountNumber + '&userName=' + userName;
    const res = this.http.get(url);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  updateAccountNickName(acctNumber: string, nickName: string): Observable<any> {
    const url = environment.OASUrl() + '/account/update-account-nickname';
    const req = {
      AccountId: acctNumber,
      NickName: nickName,
      DefaultFlag: 'Y',
    };
    const res = this.http.post(url, req);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  getReferFriendLink(acctNumber: string, email: string): Observable<any> {
    const url = environment.OASUrl() + '/partners/get-raf-link?accountNumber=' + acctNumber + '&email=' + email;
    const res = this.http.get(url);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data.Status;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  removeEmail(acctNumber: string, email: string): Observable<any> {
    const url = environment.OASUrl() + '/account/remove-email';
    const req = {
      AccountId: acctNumber,
      EmailAddress: email,
    };
    const res = this.http.post(url, req);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data.Status;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  updateMailingAddress(updateMailingAddressRequest: MailingAddress): Observable<any> {
    const url = environment.OASUrl() + '/account/update-mailing-address';
    const res = this.http.post(url, updateMailingAddressRequest);
    return res.pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data.Status;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
  }

  getServiceAgreements(accountNumber: any, userName: any, startIndex = 1, endIndex = 120): Observable<any> {
    const url = environment.OASUrl() + '/srd/get-service-agreements';
    const request = {
      AccountId: accountNumber,
      WebUserid: userName,
      startIdx: startIndex.toString(),
      endIdx: endIndex.toString(),
    };
    const res = this.http.post(url, request).pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data.ServiceAgreements === null ? [] : data.ServiceAgreements;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  getPayPlanQuote(accountNumber: string): Observable<any> {
    const url = environment.OASUrl() + '/payplan/get-payplan-quote?accoutNumber=' + accountNumber;
    const res = this.http.get(url).pipe(
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  payPlanEnroll(ppInfo: any): Observable<any> {
    const url = environment.OASUrl() + '/payplan/payplan-enroll';

    const res = this.http.post(url, ppInfo).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  billingHistory(accountNumber: string, lookBackMonths: number): Observable<any> {
    const url =
      environment.OASUrl() +
      '/account/get-account-activity?accountNumber=' +
      accountNumber +
      '&lookBackMonths=' +
      lookBackMonths;

    return this.http.get(url).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data) => {
        return data;
      }),
    );
  }

  paperlessEnroll(acct: AccountDetail): Observable<any> {
    const url = environment.OASUrl() + '/paperless/enroll?accountNumber=' + acct.AccountNumber;

    const res = this.http.post(url, acct.AccountNumber).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data: any) => {
        if (data.Error === null) {
          const notificationUrl = environment.OASUrl() + '/marketing-cloud-email/send';
          const emailData = {
            account_number: acct.AccountNumber,
            email: acct.Email,
            first_name: acct.FirstName,
            eventType: 'PaperlessEmail',
          };
          this.http.post(notificationUrl, emailData).subscribe();

          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  unauthPaperlessEnroll(encryptedAccountNumber: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };
    const url = environment.OASUrl() + '/unauthpaperless/unauth-paperless-enroll?value=' + encryptedAccountNumber;
    const res = this.http.post(url, encryptedAccountNumber, httpOptions).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  paperlessCancel(acctNumber: string): Observable<any> {
    const url = environment.OASUrl() + '/paperless/cancel?accountNumber=' + acctNumber;

    const res = this.http.post(url, acctNumber).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }

  /** helper fns */

  phoneTypeToString(phoneType: number): string {
    switch (phoneType) {
      case 0:
        return 'Mobile';
      case 1:
        return 'Landline';
      case 2:
        return 'Unconfirmed';
      case 3:
        return 'Unknown';
      default:
        return 'Unknown';
    }
  }

  unauthAccountCharacteristics(acctNumber: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ClientId: environment.ClientId,
      }),
    };
    const url =
      environment.OASUrl() +
      '/unauthaccountcharacteristics/unauth-get-account-characteristics?accountNumber=' +
      acctNumber;
    const res = this.http.post(url, acctNumber, httpOptions).pipe(
      catchError((err) => {
        throw err;
      }),
      map((data: any) => {
        if (data.Error === null) {
          return data;
        } else {
          throw new Error(data.Error?.ErrorCode);
        }
      }),
    );
    return res;
  }
}
